/**
 * THE CREW. Who cleans, and for an owner, what one clean pays.
 *
 * The split down the middle of this screen is the one from `api/src/routes/cleaners.ts`, and it is
 * worth naming because it is not obvious: the crew list is office work, and compensation terms are
 * owners only. Whoever may change a rate may change what somebody is paid.
 *
 * THE SERVER IS WHAT ENFORCES THAT. `ownersOnly()` sits on the two terms endpoints, so a manager
 * who reaches this component with a patched role in her own browser gets a 403 and nothing else.
 * The `role === 'owner'` test below decides what is worth drawing, never what is allowed, which is
 * CLAUDE.md binding rule 6: hiding something in the interface is UX and never the control.
 *
 * There is no comparison between people here: no hours, no visits per day, no ranking. The brief
 * names surveillance as a thing this must not become, and the way to say that in an interface is
 * that the screen does not exist.
 *
 * A CLEANER WHO CANNOT COME (ops #85, `docs/plan-call-off.md` sections 4, 9, 10 and 11) adds three
 * lines to one cleaner's card, each a fact and never a badge: how many call-offs in the last sixty
 * days, read for that cleaner alone and never compared or sorted, with the business's own line at
 * three; the day the owners marked the office's own checks complete, which decides who may be given a
 * client's visit at all (the business's decision of 2026-10-22), with what of the schedule is still an
 * unmarked cleaner's from before; and the one weekly slot kept open for make-ups. The list says in one
 * line when any active cleaner has no mark yet. The count reaches office roles alone because the
 * server says so; a cleaner never sees it here or anywhere in this build.
 *
 * THE LANE (ops #87, `docs/plan-two-lanes.md` section 3) is the business's word for the row's
 * classification: payroll, or fill-in on a 1099, with the day it took effect. Every office role reads
 * it; an owner sets and clears it through its own door, as the checks mark is set. It decides whether
 * the company checklist is shown on that cleaner's sheet, and nothing on this screen besides.
 *
 * THE KEYS A CLEANER HOLDS (ops #100), on their card: each key with its home and since when, each
 * opening the home's page, or "Holds no keys". It is the offboarding list, so a read that failed says
 * so and never that the cleaner holds none.
 *
 * The one figure typed on this screen is what one clean pays a cleaner. That is compensation and
 * not a client price. No client price is typed anywhere in this product: those come from the
 * published card through GET /api/catalog, and there is no edit screen for them.
 *
 * AND IT IS WHERE A SIGN-IN IS SHUT. Ending somebody's cleaner row stops her being given work and
 * empties every scoped read she can make, and it does nothing whatever to her sign-in: the cookie
 * in her phone still resolves to a live `users` row and she is still admitted, to a product with
 * nothing in it. The column that actually shuts the door is `users.status`, and
 * `api/src/middleware/identity.ts` reads it on every request. `docs/handover.md` section 3 item 7
 * asked for the control here, beside the people it is about, and this is it: owners only, two
 * presses, and quiet, because shutting a door is a decision somebody made rather than a gap the
 * product is asking her to close.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CLEANER_STATUSES, humanise, type Classification, type CleanerStatus } from '@fk/shared';
import {
  ApiError,
  addTerm,
  createCleaner,
  getCleaner,
  getCleanerKeys,
  listCleaners,
  patchCleaner,
  setCleanerChecks,
  setCleanerLane,
  listTerms,
  listUsers,
  setUserStatus,
  type CleanerKey,
  type CleanerRow,
  type Me,
  type OfficeUser,
  type TermRow,
} from '../api.ts';
import { crewChecksNote, unmarkedWorkWords } from '../checks.ts';
import { Card, Detail, Pill, READING_WIDTH, ScreenTitle } from '../components/AppShell.tsx';
import { CallLink } from '../components/CallLink.tsx';
import { Button, SelectField, TextField } from '../components/Field.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import {
  E164,
  centsFromDollars,
  dayOf,
  formatDay,
  formatLocalWindow,
  mediumDay,
  money,
  orNull,
  toE164,
} from '../format.ts';
import { keyName } from '../keys.ts';
import { officeUserLabel, signInDone, statusAfter, type SignInAction } from '../people.ts';

export function Crew({ me }: { me: Me }) {
  const [rows, setRows] = useState<readonly CleanerRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    listCleaners()
      .then((answer) => {
        if (live) setRows(answer.cleaners);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The crew did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  return (
    <div className={READING_WIDTH}>
      <ScreenTitle title="Crew" subtitle="Who cleans, and how to reach them." />

      <AddCleaner onSaved={reload} />

      {error ? (
        <ErrorNotice title="The crew did not load" message={error.message} onRetry={reload} />
      ) : rows === null ? (
        <Busy what="the crew" />
      ) : rows.length === 0 ? (
        <EmptyNotice title="Nobody on the crew yet">
          <p>Add the first person above. An applicant is a real state, so they can go in now.</p>
        </EmptyNotice>
      ) : (
        <>
          {crewChecksNote(rows) ? (
            <p className="mb-3 text-sm" data-crew-checks-note>
              {crewChecksNote(rows)}
            </p>
          ) : null}
          <ul className="space-y-3">
            {rows.map((cleaner) => (
              <li key={cleaner.id}>
                <Card>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-serif text-lg leading-tight">{cleaner.displayName}</h2>
                    <Pill tone={cleaner.status === 'active' ? 'sage' : 'quiet'}>
                      {humanise(cleaner.status)}
                    </Pill>
                  </div>

                  <div className="mt-3">
                    <CallLink phoneE164={cleaner.phoneE164} />
                  </div>

                  <LaneLine cleaner={cleaner} me={me} onSaved={reload} />

                  <dl className="mt-2">
                    <Detail label="Started" value={formatDay(cleaner.startedOn)} />
                    <Detail label="Ended" value={formatDay(cleaner.endedOn)} />
                  </dl>

                  <CallOffCount cleanerId={cleaner.id} />
                  <ChecksMark cleaner={cleaner} me={me} onSaved={reload} />
                  <MakeUpSlotLine cleaner={cleaner} onSaved={reload} />
                  <KeysHeld cleanerId={cleaner.id} />

                  {me.role === 'owner' ? <Compensation cleanerId={cleaner.id} /> : null}
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}

      <SignIns me={me} />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The keys a cleaner holds (ops #100): the offboarding list.
 * ------------------------------------------------------------------ */

/**
 * EVERY KEY THIS CLEANER HOLDS NOW, across homes, each opening the home's page where its return is
 * recorded. A read that failed says so: "Holds no keys" is said only when the server said it.
 */
function KeysHeld({ cleanerId }: { cleanerId: string }) {
  const [keys, setKeys] = useState<readonly CleanerKey[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    getCleanerKeys(cleanerId)
      .then((answer) => {
        if (live) setKeys(answer.keys);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [cleanerId]);
  return (
    <div className="mt-2" data-keys-held>
      <p className="text-sm font-medium">Keys held</p>
      {failed ? (
        <p className="text-sm text-coral">The keys did not load.</p>
      ) : keys === null ? null : keys.length === 0 ? (
        <p className="text-sm text-navy/70">Holds no keys</p>
      ) : (
        <ul className="flex flex-col">
          {keys.map((key) => {
            const since = dayOf(key.since);
            return (
              <li key={`${key.home.id}\n${key.keyKind}\n${key.keyLabel}`}>
                <Link
                  to={`/homes/${key.home.id}`}
                  className="flex min-h-11 flex-col justify-center py-1 text-sm desk:min-h-9"
                >
                  <span className="font-medium underline">{keyName(key)}</span>
                  <span className="text-xs leading-4 text-navy/65">
                    {key.home.label ?? key.home.addressLine1}
                    {since ? `, since ${mediumDay(since)}` : ''}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * A cleaner who cannot come (ops #85): the count, the checks, the slot.
 * ------------------------------------------------------------------ */

/** The business's own line for a conversation (`call-off-plan.md` section 5). */
const CONVERSATION_AT = 3;

/** The words of the count line, for one cleaner and nobody else. */
export function callOffCountWords(count: number): string {
  return count >= CONVERSATION_AT
    ? `${count} in the last 60 days: the business's line for a conversation (call-off-plan.md section 5)`
    : `Call-offs in the last 60 days: ${count}`;
}

/**
 * HOW MANY TIMES THIS CLEANER COULD NOT COME, in the last sixty days, read for them alone. A count,
 * never a ranking: nothing on this screen orders the crew by it, and nothing automatic follows it.
 */
function CallOffCount({ cleanerId }: { cleanerId: string }) {
  const [count, setCount] = useState<number | null>(null);
  const [unmarked, setUnmarked] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    getCleaner(cleanerId)
      .then((answer) => {
        if (!live) return;
        if (answer.callOffs) setCount(answer.callOffs.inLast60Days);
        // The same read says whether the owners' mark is set, and what is still theirs without it.
        setUnmarked(unmarkedWorkWords(answer.checks));
      })
      .catch(() => {
        // The line is not drawn when the read fails: the rest of the card stands on its own.
      });
    return () => {
      live = false;
    };
  }, [cleanerId]);
  return (
    <>
      {count === null ? null : (
        <p className="mt-2 text-sm" data-call-off-count>
          {callOffCountWords(count)}
        </p>
      )}
      {unmarked ? (
        <p className="mt-2 text-sm text-coral" data-unmarked-work>
          {unmarked}
        </p>
      ) : null}
    </>
  );
}

/**
 * THE DAY THE OFFICE'S OWN CHECKS WERE COMPLETE, a fact and never a badge. Owners set and clear it;
 * the server is what says so, and whoever is given a client's visit must carry it (ops #85).
 */
function ChecksMark({
  cleaner,
  me,
  onSaved,
}: {
  cleaner: CleanerRow;
  me: Me;
  onSaved: () => void;
}) {
  const [day, setDay] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(completeOn: string | null) {
    setBusy(true);
    setProblem(null);
    try {
      await setCleanerChecks(cleaner.id, completeOn);
      setDay('');
      onSaved();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'It was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2" data-checks>
      <p className="text-sm">
        {cleaner.checksCompleteOn
          ? `Checks complete on ${formatDay(cleaner.checksCompleteOn)}`
          : 'Checks not complete'}
      </p>
      {me.role === 'owner' ? (
        <div className="mt-1">
          {problem ? <ErrorNotice title="Not saved" message={problem} /> : null}
          <div className="flex flex-wrap items-end gap-2">
            <TextField
              id={`checks-${cleaner.id}`}
              label="Checks complete on"
              type="date"
              value={day}
              onChange={setDay}
            />
            <Button tone="quiet" onClick={() => save(day)} disabled={busy || day === ''}>
              Mark complete
            </Button>
            {cleaner.checksCompleteOn ? (
              <Button tone="quiet" onClick={() => save(null)} disabled={busy}>
                Clear
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The lane (ops #87): payroll, or fill-in on a 1099, set by an owner.
 * ------------------------------------------------------------------ */

const LANE_LABELS: Record<Classification, string> = {
  payroll_w2: 'Payroll',
  contractor_1099: 'Fill-in, 1099',
};
const LANE_VALUES = ['payroll_w2', 'contractor_1099'] as const satisfies readonly Classification[];

/** The lane in the business's own words (`first-cleaner.md`, the two lanes), with its day. */
export function laneWords(classification: Classification | null, changedOn: string | null): string {
  if (classification === null) return 'Lane: not set yet';
  const lane = classification === 'payroll_w2' ? 'payroll' : 'fill-in, 1099';
  const since = formatDay(changedOn);
  return since ? `Lane: ${lane}, since ${since}` : `Lane: ${lane}`;
}

/**
 * THE LANE, a fact on the record and never a badge (ops #87, `docs/plan-two-lanes.md` section 3).
 * Every office role reads it; an owner sets and clears it, and the server's `ownersOnly()` on the
 * lane door is what says so. It decides whether the company checklist is shown on the cleaner's own
 * sheet, and nothing else.
 */
function LaneLine({ cleaner, me, onSaved }: { cleaner: CleanerRow; me: Me; onSaved: () => void }) {
  const [lane, setLane] = useState<Classification | ''>('');
  const [day, setDay] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(classification: Classification | null, changedOn: string | null) {
    setBusy(true);
    setProblem(null);
    try {
      await setCleanerLane(cleaner.id, classification, changedOn);
      setLane('');
      setDay('');
      onSaved();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'It was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2" data-lane>
      <p className="text-sm">
        {laneWords(cleaner.classification, cleaner.classificationChangedOn)}
      </p>
      {me.role === 'owner' ? (
        <div className="mt-1">
          {problem ? <ErrorNotice title="Not saved" message={problem} /> : null}
          <div className="flex flex-wrap items-end gap-2">
            <SelectField
              id={`lane-${cleaner.id}`}
              label="Lane"
              value={lane}
              onChange={setLane}
              options={LANE_VALUES}
              toLabel={(value) => LANE_LABELS[value]}
              placeholder="Choose a lane"
            />
            <TextField
              id={`lane-day-${cleaner.id}`}
              label="Since"
              type="date"
              value={day}
              onChange={setDay}
            />
            <Button
              tone="quiet"
              onClick={() => save(lane === '' ? null : lane, day)}
              disabled={busy || lane === '' || day === ''}
            >
              Save the lane
            </Button>
            {cleaner.classification ? (
              <Button tone="quiet" onClick={() => save(null, null)} disabled={busy}>
                Clear the lane
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const WEEKDAY_WORDS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const WEEKDAY_VALUES = ['0', '1', '2', '3', '4', '5', '6'] as const;

/** The one weekly slot kept open for make-ups: set, changed or cleared by the office. */
function MakeUpSlotLine({ cleaner, onSaved }: { cleaner: CleanerRow; onSaved: () => void }) {
  const has =
    cleaner.makeUpWeekday != null &&
    Boolean(cleaner.makeUpWindowStartLocal) &&
    Boolean(cleaner.makeUpWindowEndLocal);
  const [weekday, setWeekday] = useState<(typeof WEEKDAY_VALUES)[number] | ''>('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(clear: boolean) {
    setBusy(true);
    setProblem(null);
    try {
      await patchCleaner(
        cleaner.id,
        clear
          ? { makeUpWeekday: null, makeUpWindowStartLocal: null, makeUpWindowEndLocal: null }
          : {
              makeUpWeekday: weekday === '' ? null : Number(weekday),
              makeUpWindowStartLocal: start || null,
              makeUpWindowEndLocal: end || null,
            },
      );
      onSaved();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'It was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="mt-2" data-make-up-slot>
      <summary className="cursor-pointer text-sm">
        {has
          ? `Make-up slot: ${WEEKDAY_WORDS[cleaner.makeUpWeekday!]}s, ${formatLocalWindow(cleaner.makeUpWindowStartLocal!, cleaner.makeUpWindowEndLocal!)}`
          : 'No make-up slot kept'}
      </summary>
      <div className="mt-2">
        {problem ? <ErrorNotice title="Not saved" message={problem} /> : null}
        <SelectField
          id={`slot-day-${cleaner.id}`}
          label="Weekday kept open"
          value={weekday}
          onChange={setWeekday}
          options={WEEKDAY_VALUES}
          toLabel={(value) => WEEKDAY_WORDS[Number(value)]!}
          placeholder="Choose a weekday"
        />
        <TimeBox id={`slot-start-${cleaner.id}`} label="From" value={start} onChange={setStart} />
        <TimeBox id={`slot-end-${cleaner.id}`} label="Until" value={end} onChange={setEnd} />
        <div className="flex flex-wrap gap-2">
          <Button tone="quiet" onClick={() => save(false)} disabled={busy}>
            Keep this slot open
          </Button>
          {has ? (
            <Button tone="quiet" onClick={() => save(true)} disabled={busy}>
              Keep no slot
            </Button>
          ) : null}
        </div>
      </div>
    </details>
  );
}

function TimeBox({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="mb-3">
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type="time"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 block min-h-11 w-full rounded-lg border border-navy/25 bg-parchment px-3 py-2 text-navy desk:min-h-9"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Who can sign in, and the one control that changes it.
 * ------------------------------------------------------------------ */

/**
 * WHO THE PRODUCT WILL ADMIT, which is a different list from who cleans.
 *
 * There is no self-registration and never will be: a row here exists because an owner ran the
 * enrolment script with a name in it. So this list is short, it changes about once a year, and the
 * only thing worth doing to it from a screen is shutting a door somebody no longer needs open.
 *
 * THE CONTROL IS OWNERS ONLY AND THE SERVER IS WHAT SAYS SO. The `role === 'owner'` test below
 * decides what is worth drawing, never what is allowed: CLAUDE.md binding rule 6. A manager who
 * reached this component with a patched role in her own browser gets a refusal from the door and
 * nothing else.
 *
 * IT IS NEVER CORAL. Coral means a gap somebody has to close, and a person who can still sign in
 * is not a gap; it is the ordinary state of everybody on the list. Two presses, quiet both times.
 */
function SignIns({ me }: { me: Me }) {
  const [people, setPeople] = useState<readonly OfficeUser[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reread = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setProblem(null);
    listUsers()
      .then((answer) => {
        if (live) setPeople(answer.users);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setProblem(thrown instanceof ApiError ? thrown.message : 'The sign-ins did not load.');
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  return (
    <Card className="mt-5">
      <h2 className="font-serif text-lg leading-7">Who can sign in</h2>
      <p className="mt-1 text-sm text-navy/70">
        Everybody the product will admit. Nobody adds themselves: a row here exists because an owner
        put it there. Suspending somebody shuts the door at their next request and removes nothing
        at all, so what they did stays on the record with their name against it.
      </p>

      {said ? (
        <div className="mt-3">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      {problem ? (
        <div className="mt-3">
          <ErrorNotice title="The sign-ins did not load" message={problem} onRetry={reread} />
        </div>
      ) : people === null ? (
        <Busy what="the sign-ins" />
      ) : people.length === 0 ? (
        <p className="mt-3 text-sm text-navy/70">Nobody is enrolled.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {people.map((person) => (
            <li key={person.id}>
              <SignIn
                person={person}
                me={me}
                onDone={(sentence) => {
                  setSaid(sentence);
                  reread();
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function SignIn({
  person,
  me,
  onDone,
}: {
  person: OfficeUser;
  me: Me;
  onDone: (sentence: string) => void;
}) {
  const line = officeUserLabel(person, me.userId);
  const [asking, setAsking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const action = me.role === 'owner' ? line.action : null;

  async function run(action: SignInAction) {
    setBusy(true);
    setProblem(null);
    try {
      await setUserStatus(person.id, statusAfter(action));
      setAsking(false);
      onDone(signInDone(person, action));
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not changed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-navy/15 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{line.name}</span>
        <Pill>{line.label}</Pill>
      </div>
      <p className="mt-1 text-sm text-navy/70">{line.sentence}</p>

      {problem ? (
        <div className="mt-3">
          <ErrorNotice title="Not changed" message={problem} />
        </div>
      ) : null}

      {action !== null ? (
        <div className="mt-3">
          {asking ? (
            <div className="rounded-lg border border-navy/20 p-3">
              <p className="text-sm">{line.question}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button tone="quiet" disabled={busy} onClick={() => void run(action)}>
                  {busy ? 'Changing it' : `Yes, ${line.actionWords?.toLowerCase()}`}
                </Button>
                <Button tone="quiet" onClick={() => setAsking(false)} disabled={busy}>
                  Never mind
                </Button>
              </div>
            </div>
          ) : (
            <Button tone="quiet" onClick={() => setAsking(true)}>
              {line.actionWords}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AddCleaner({ onSaved }: { onSaved: () => void }) {
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<CleanerStatus>('applicant');
  const [startedOn, setStartedOn] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    if (!displayName.trim()) {
      setProblem('They need a name.');
      return;
    }
    if (phone.trim() && !E164.test(toE164(phone) ?? '')) {
      setProblem('That phone number does not look right. Ten digits is enough.');
      return;
    }

    setSaving(true);
    try {
      await createCleaner({
        displayName: displayName.trim(),
        phoneE164: phone.trim() ? toE164(phone) : null,
        status,
        startedOn: orNull(startedOn),
      });
      setDisplayName('');
      setPhone('');
      setStatus('applicant');
      setStartedOn('');
      onSaved();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'They were not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="mb-5 rounded-xl border border-navy/12 bg-parchment p-4">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
        Add somebody to the crew
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not saved" message={problem} />
          </div>
        ) : null}
        <TextField
          id="cleaner-name"
          label="Name"
          required
          value={displayName}
          onChange={setDisplayName}
          hint="What goes on a schedule. Their legal name is a separate field and is only needed for a W-9."
          autoComplete="off"
        />
        <TextField
          id="cleaner-phone"
          label="Phone"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={setPhone}
          hint="Somebody at a locked door at 8am has to be reachable."
          autoComplete="off"
        />
        <SelectField
          id="cleaner-status"
          label="Where they are now"
          value={status}
          onChange={setStatus}
          options={CLEANER_STATUSES}
          toLabel={humanise}
        />
        <TextField
          id="cleaner-started"
          label="Started"
          type="date"
          value={startedOn}
          onChange={setStartedOn}
        />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Add to the crew'}
        </Button>
      </form>
    </details>
  );
}

/**
 * Owners only, and loaded only when the panel is opened, so a crew list of ten costs one request
 * rather than eleven. A 403 from here means the server disagreed about the role, which is the
 * server being right.
 *
 * A FAILED READ AND A REFUSED WRITE ARE TWO DIFFERENT FAILURES, and this panel used to hold them
 * both in one string under one heading. So a rate history that did not load said "Not saved" over
 * a form nobody had submitted, and there was no way to ask for it again: the only thing that
 * re-read it was submitting a new rate, which is the last thing somebody should do while she
 * cannot see what the rate already is. Two pieces of state now, and the read one carries the Try
 * again every other screen in this product offers.
 */
function Compensation({ cleanerId }: { cleanerId: string }) {
  const [open, setOpen] = useState(false);
  const [terms, setTerms] = useState<readonly TermRow[] | null>(null);
  const [readProblem, setReadProblem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dollars, setDollars] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const reread = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setReadProblem(null);
    listTerms(cleanerId)
      .then((answer) => {
        if (live) setTerms(answer.terms);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setReadProblem(
          thrown instanceof ApiError ? thrown.message : 'The rate history did not load.',
        );
      });
    return () => {
      live = false;
    };
  }, [cleanerId, open, attempt]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSaved(false);
    const cents = centsFromDollars(dollars);
    if (cents === null) {
      setError('That is not an amount. Type it in dollars, like 95 or 95.50.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) {
      setError('A new rate needs the day it takes effect.');
      return;
    }

    setSaving(true);
    try {
      await addTerm(cleanerId, { perCleanCents: cents, effectiveFrom });
      setDollars('');
      setEffectiveFrom('');
      setSaved(true);
      reread();
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : 'That rate was not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details
      className="mt-3 rounded-lg border border-navy/15 p-3"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
        What one clean pays
      </summary>

      <p className="mt-2 text-xs text-navy/65">
        Compensation, not a client price. A new rate closes the one before it rather than editing
        it, because a retrofitted rate is a guess about what October paid.
      </p>

      {readProblem ? (
        <div className="mt-3">
          <ErrorNotice
            title="The rate history did not load"
            message={readProblem}
            onRetry={reread}
          />
        </div>
      ) : null}

      {error ? (
        <div className="mt-3">
          <ErrorNotice title="Not saved" message={error} />
        </div>
      ) : null}

      {saved ? (
        <div className="mt-3">
          <SavedNotice>The new rate is in force from the day you gave.</SavedNotice>
        </div>
      ) : null}

      {open && terms === null && !readProblem ? <Busy what="the rate history" /> : null}

      {terms && terms.length > 0 ? (
        <ul className="mt-3 space-y-1 text-sm">
          {terms.map((term) => (
            <li key={term.id} className="flex flex-wrap justify-between gap-2">
              <span className="font-medium">{money(term.perCleanCents)} a clean</span>
              <span className="text-navy/70">
                {formatDay(term.effectiveFrom)}
                {term.effectiveTo ? ` to ${formatDay(term.effectiveTo)}` : ', still in force'}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {terms && terms.length === 0 ? (
        <p className="mt-3 text-sm text-navy/70">No rate agreed yet.</p>
      ) : null}

      <form className="mt-4" onSubmit={submit} noValidate>
        <TextField
          id={`rate-${cleanerId}`}
          label="What one clean pays"
          inputMode="numeric"
          value={dollars}
          onChange={setDollars}
          hint="Dollars, like 95 or 95.50. The same figure for every home."
          placeholder="95.00"
        />
        <TextField
          id={`rate-from-${cleanerId}`}
          label="In force from"
          type="date"
          value={effectiveFrom}
          onChange={setEffectiveFrom}
          hint="Has to be after the day the current rate started."
        />
        <Button type="submit" tone="quiet" full disabled={saving}>
          {saving ? 'Saving' : 'Set this rate'}
        </Button>
      </form>
    </details>
  );
}
